import React, { useContext, useEffect, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { LanguageContext } from "@lib/i18n/LanguageProvider";
import { Helmet } from "react-helmet-async";

import { CustomButton, CustomForm, CustomModal, CustomText } from "albinasoft-ui-package";
import {
  FontSize,
  LineHeight,
  TextType
} from "albinasoft-ui-package/dist/components/CustomText";
import { CustomTimeline, DotColor } from "albinasoft-ui-package/dist/components/CustomTimeline";
import ClientTrackApi from "api/ClientTrackApi";
import { dateFormatter } from "@utils/dateFormatter";
import { Button } from "react-bootstrap";
import { ElementType } from "@components/CustomForm";
import { InputType } from "@components/CustomInput";

// Layout context tipi
type LayoutContextType = {
  setLayoutContext: (title: string) => void;
  setLayoutHeaderImage: (url: string) => void;
  setPageTitle: (pageTitle: string) => void;
  setPageSummary: (pageSummary: string) => void;
  setVideoHelpUrl: (helpUrl: string) => void;
  setShowHeader: (showHeader: boolean) => void;
  setShowSidebar: (showSidebar: boolean) => void;
  setShowSummaryOnMobile: (showSummaryOnMobile: boolean) => void;
};

interface PageConfig {
  title: string;
  summary: string;
  helpUrl: string;
  headerImageLink: string;
}

const RequestTrackingScreen: React.FC = () => {
  const {
    setPageTitle,
    setPageSummary,
    setVideoHelpUrl,
    setShowHeader,
    setShowSidebar,
    setShowSummaryOnMobile,
    setLayoutHeaderImage,
  } = useOutletContext<LayoutContextType>();

  const { translations = {} } = useContext(LanguageContext) || {};
  const { id, guid } = useParams();
  const [config, setConfig] = useState<PageConfig | null>(null);
  const [response, setResponse] = useState<any | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const [previewFileUrl, setPreviewFileUrl] = useState<string | null>(null);
  const modalSize = "xl";

  const [showModal, setShowModal] = useState(false);
  const [reActivateReason, setReActivateReason] = useState("");

  // Yardımcı dosya önizleme sabitleri
  const previewableExtensions = [".png", ".jpg", ".jpeg", ".pdf"];
  const imageExtensions = [".png", ".jpg", ".jpeg"];
  const fileTypeIcons: Record<string, JSX.Element> = {
    ".pdf": <i className="fa fa-file-pdf-o" />,
    ".docx": <i className="fa fa-file-word-o" />,
    ".xlsx": <i className="fa fa-file-excel-o" />
  };
  const defaultFileIcon = <i className="fa fa-file-o" />;

  // Sayfa ayarları
  const fetchData = async () => {
    if (!id || !guid) return;
    const result = await ClientTrackApi.getRequestTrackTimeLine(Number(id), guid);
    if (result) {
      setResponse(result);
      const pageData: PageConfig = {
        title: `${result.clientName} | Talep Takip`,
        summary: "",
        helpUrl: "",
        headerImageLink: result.clientBannerUrl || "https://dummyimage.com/2400X300/000000/fff.jpg"
      };
      setConfig(pageData);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id, guid]);

  // Layout ayarlarını güncelle
  useEffect(() => {
    if (!config) return;
    setPageTitle(response.clientName || "");

    setPageSummary(config.summary);
    setVideoHelpUrl(config.helpUrl);
    setShowHeader(true);
    setShowSidebar(false);
    setShowSummaryOnMobile(false);
    setLayoutHeaderImage(config.headerImageLink);
  }, [config]);

  const handleSubmit = async () => {
    if (!id || !guid) return;

    const result = await ClientTrackApi.setReActivateRequest(Number(id), guid, {
      reActivateReason,
    });

    if (result?.responseCode === 200) {
      // toast.success("Talep başarıyla yeniden aktifleştirildi.");
      fetchData();
      setShowModal(false);
    } else {
      // toast.error(result?.text || "Aktifleştirme işlemi başarısız oldu.");
    }
  };

  if (!config) return <div>Yükleniyor…</div>;

  return (
    <div className="content-inner container-fluid pb-0">
      <Helmet>
        <title>{config.title}</title>
      </Helmet>

      {response ? (
        <>
          <div className="row">
            <div className="col-lg-12 col-12">
              <div className="card">
                <div className="card-body">

                  {/* {response.reActiveRequest && ( */}
                  <div className="mt-2">
                    <CustomText
                      value={response.userMessage}
                      textType={TextType.PARAGRAPH}
                      fontSize={FontSize.XXS}
                      lineHeight={LineHeight.S}
                    />
                  </div>
                  {/* )} */}
                </div>
              </div>
            </div>

          </div>

          <div className="col-12 mt-3">
            <CustomTimeline
              timelineElements={response.trackListDetails.map((item: any) => ({
                dotColor: DotColor.PRIMARY,
                title: item.header,
                mainTime: dateFormatter(item.date),
                description: item.detail || "",
                // additionalInfos: item.detail
                //   ? [
                //     {
                //       time: dateFormatter(item.date),
                //       description: item.detail,
                //     }
                //   ]
                //   : [],
              }))}
            />
          </div>
        </>
      ) : (
        <CustomText
          value="Yükleniyor..."
          textType={TextType.PARAGRAPH}
          fontSize={FontSize.XS}
          lineHeight={LineHeight.XXS}
        />
      )}

      {response.reActiveRequest && (<>
        <div className="col-12 mt-3">
          <CustomButton label="Başvuruyu Yeniden Aktifleştir" className="btn bg-soft-primary" onClick={() => setShowModal(true)} />
        </div>

      </>
      )}

      <CustomModal
        show={showModal}
        title="Talebi Yeniden Aktifleştir"
        onHide={() => setShowModal(false)}
      >
        <CustomForm
          elements={[
            {
              id: "reActivateReason",
              label: "Aktifleştirme Gerekçesi",
              type: ElementType.TEXTAREA,
              // inputType: InputType.TEXT,
              value: reActivateReason,
              onChange: (e: any) => setReActivateReason(e.target.value),
              // required: true,
              // rows: 4,
              colClass: "col-12",
            },
          ]}
          onSubmit={handleSubmit}
          confirmLabel="Kaydet"
        />
      </CustomModal>

      {/* Dosya önizleme modal */}
      <CustomModal
        show={previewModalOpen}
        title="Önizleme"
        size={modalSize}
        onHide={() => {
          setPreviewModalOpen(false);
          setPreviewFileUrl(null);
        }}
        bodyStyle={{ padding: 0 }}
      >
        {previewFileUrl &&
          (() => {
            const extension = previewFileUrl.substring(previewFileUrl.lastIndexOf('.')).toLowerCase();
            if (imageExtensions.includes(extension)) {
              return (
                <img src={previewFileUrl} alt="Önizleme" style={{ width: "100%" }} />
              );
            } else if (extension === ".pdf") {
              return (
                <iframe
                  src={previewFileUrl}
                  style={{ width: "100%", height: "100%" }}
                  title="PDF Önizleme"
                />
              );
            } else {
              return <div style={{ padding: "1rem" }}>Önizleme desteklenmiyor.</div>;
            }
          })()}
      </CustomModal>
    </div>
  );
};

export default RequestTrackingScreen;
